// @ai-generated 2026-08-11T00:00:00Z by niklas.eicker@tngtech.com using Claude Opus 5 (1M context)
import { useSession } from '@Contexts/session';
import { useState } from 'react';
import {
    type ApiWorkflowStoryStep,
    EnumWorkflowStepEffect,
    EnumWorkflowStepStatus,
    EnumWorkflowStoryMode,
} from '@Model';
import { readValidationOutcome } from '@utils/toolCallEnvelope';
import { RejectedRecordTable } from './RejectedRecordTable';
import { validationToolFor } from './validationTools';
import classes from './WorkflowStepList.module.css';

export interface WorkflowStepListProps {
    steps: ApiWorkflowStoryStep[];
    /** A `Preview` is a plan: no status to show, so the marker carries the step's position instead. */
    mode?: EnumWorkflowStoryMode;
}

/**
 * One flat, numbered row per user-meaningful step. Flat by design: a workflow is a nested tree, but
 * nesting belongs in the step detail, not here. Serves preview, live and history alike.
 */
export function WorkflowStepList({ steps, mode = EnumWorkflowStoryMode.Live }: WorkflowStepListProps) {
    const isPlan = mode === EnumWorkflowStoryMode.Preview;

    return (
        <div className={classes.list}>
            {steps.map((step) => (
                <WorkflowStepRow key={step.action_id} step={step} isPlan={isPlan} />
            ))}
        </div>
    );
}

/**
 * A failed step's outcome is a list of messages plus a payload, which would crowd out the row it
 * belongs to. The status word always holds the right-hand slot — it used to be replaced by the
 * summary, so the one row that had most to say was the only one not saying how it went — and the
 * detail lives behind a disclosure.
 */
function WorkflowStepRow({ step, isPlan }: { step: ApiWorkflowStoryStep; isPlan: boolean }) {
    const [expanded, setExpanded] = useState(false);
    const {
        current: { subpath },
    } = useSession();
    const appearance = STATUS_APPEARANCE[step.step_status];
    const failed = step.step_status === EnumWorkflowStepStatus.Failed;
    // A non-failed step's summary is its authored outcome line, short enough to read in place.
    const inlineSummary = failed ? undefined : step.summary;
    const messages = failed ? failureMessages(step.summary) : [];

    // A validation step's envelope names every rejected record and links to it, which the message
    // bullets cannot: the reasons are prose, and only the payload carries the ids (DEVCLD-2375).
    const rejected = failed ? readRejected(step) : undefined;
    const expandable = failed && (messages.length > 0 || rejected !== undefined || Boolean(step.detail));

    return (
        <div className={failed ? `${classes.card} ${classes.cardFailed}` : classes.card}>
            <div className={classes.header}>
                {isPlan ? (
                    <span className={`${classes.statusDot} ${classes.dotPlan}`}>{step.index}</span>
                ) : (
                    <span className={`${classes.statusDot} ${appearance.dotClass}`} title={appearance.label}>
                        {appearance.glyph}
                    </span>
                )}
                <span className={classes.title}>{isPlan ? step.title : `${step.index} · ${step.title}`}</span>
                {step.branch && <span className={`${classes.badge} ${classes.branchBadge}`}>{step.branch}</span>}
                {step.effect === EnumWorkflowStepEffect.Write && (
                    <span className={`${classes.badge} ${classes.writeBadge}`}>ändert Daten</span>
                )}
                {!step.supported && (
                    <span className={`${classes.badge} ${classes.unsupportedBadge}`}>nicht unterstützt</span>
                )}
                {inlineSummary && <span className={classes.summary}>{inlineSummary}</span>}
                {!isPlan && (
                    <span
                        className={
                            inlineSummary ? classes.statusLabel : `${classes.statusLabel} ${classes.statusLabelAlone}`
                        }
                    >
                        {appearance.label}
                    </span>
                )}
                {expandable && (
                    <button
                        type="button"
                        onClick={() => setExpanded(!expanded)}
                        className={classes.disclosure}
                        aria-expanded={expanded}
                        title={expanded ? 'Details ausblenden' : 'Details anzeigen'}
                    >
                        {expanded ? '▾' : '▸'}
                    </button>
                )}
            </div>
            {expanded && (
                <div className={classes.details}>
                    {rejected !== undefined ? (
                        <RejectedRecordTable outcome={rejected.outcome} tool={rejected.tool} subpath={subpath} />
                    ) : (
                        messages.length > 0 && (
                            <ul className={classes.messageList}>
                                {messages.map((message, position) => (
                                    <li key={position} className={classes.message}>
                                        {message}
                                    </li>
                                ))}
                            </ul>
                        )
                    )}
                    {step.detail && <TechnicalDetail detail={step.detail} />}
                </div>
            )}
            {step.progress && <ProgressBar current={step.progress.current} total={step.progress.total} />}
        </div>
    );
}

/** The raw command envelope, nested one level deeper so it never competes with the messages. */
function TechnicalDetail({ detail }: { detail: string }) {
    const [shown, setShown] = useState(false);

    return (
        <div>
            <button type="button" onClick={() => setShown(!shown)} className={classes.detailToggle}>
                {shown ? '▾' : '▸'} Technische Details
            </button>
            {shown && <pre className={classes.payload}>{prettyJson(detail)}</pre>}
        </div>
    );
}

/**
 * The records a validation step rejected, or undefined when the step is not a validation call or its
 * envelope holds no rejections. Undefined is the signal to fall back to the message bullets, so a
 * step whose payload we cannot read never renders as an empty table.
 */
function readRejected(step: ApiWorkflowStoryStep) {
    const tool = validationToolFor(step.tool_name);
    if (tool === undefined || !step.detail) {
        return undefined;
    }

    const outcome = readValidationOutcome(step.detail);
    if (outcome === undefined || outcome.hardFailed.length + outcome.softFailed.length === 0) {
        return undefined;
    }

    return { outcome, tool };
}

/**
 * The failure text as one entry per message. The backend writes a lead line followed by one
 * `- [Level/Code] …` bullet per message, so the bullets are the messages and the lead line is not.
 */
function failureMessages(summary: string): string[] {
    const lines = summary
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
    const bullets = lines.filter((line) => line.startsWith('- '));

    return bullets.length > 0 ? bullets.map((line) => line.slice(2)) : lines;
}

/** Indented JSON when it parses, the raw text when it does not — never an error in place of content. */
function prettyJson(detail: string): string {
    try {
        return JSON.stringify(JSON.parse(detail), null, 2);
    } catch {
        return detail;
    }
}

/** Hidden when the total is unknown: "12 von 17" without a real total would be a guess. */
function ProgressBar({ current, total }: { current: number; total: number | undefined }) {
    if (total === undefined) {
        return null;
    }

    return (
        <div className={classes.progressTrack}>
            {/* Width is the datum itself, so it stays inline rather than becoming a class. */}
            <div className={classes.progressFill} style={{ width: `${Math.min(100, (current / total) * 100)}%` }} />
        </div>
    );
}

const STATUS_APPEARANCE: Record<EnumWorkflowStepStatus, { dotClass: string; glyph: string; label: string }> = {
    [EnumWorkflowStepStatus.Pending]: { dotClass: classes.dotPending, glyph: '', label: 'ausstehend' },
    [EnumWorkflowStepStatus.Running]: { dotClass: classes.dotRunning, glyph: '↻', label: 'läuft' },
    [EnumWorkflowStepStatus.Done]: { dotClass: classes.dotDone, glyph: '✓', label: 'erledigt' },
    [EnumWorkflowStepStatus.WaitingForUser]: { dotClass: classes.dotRunning, glyph: '?', label: 'wartet auf dich' },
    [EnumWorkflowStepStatus.Skipped]: { dotClass: classes.dotSkipped, glyph: '–', label: 'nicht ausgeführt' },
    [EnumWorkflowStepStatus.Failed]: { dotClass: classes.dotFailed, glyph: '✕', label: 'fehlgeschlagen' },
};
